/**
 * imageInfoCore.js — СТРОКИ РАЗДЕЛА «ИНФОРМАЦИЯ» ЛЕВОЙ ПАНЕЛИ. [12.0.25]
 *
 * Чистый модуль: ни DOM, ни Cornerstone — только данные на входе и строки на
 * выходе. Проводка к странице — в imageInfo.js.
 *
 * Почему модуль вообще понадобился. В разметке раздела шесть строк:
 * Пациент, Исследование, Серия, Изображение, Шаг, Матрица. Заполнялись из
 * них ДВЕ — `updateOverlays` писал «Patient: …» и «Series: …», причём
 * по-английски, поверх русских заготовок. Остальные четыре не писал никто:
 * поиск по всему проекту находил их только в index.html. Владелец увидел
 * это на скриншоте — половина раздела стояла прочерками при открытом
 * исследовании.
 *
 * Здесь одна функция на каждую строку и одна — на все сразу, чтобы подписи,
 * порядок и прочерки жили в одном месте, а не в трёх (заполнение, очистка
 * при закрытии исследования, мобильный ящик).
 *
 * EXPORTS: ПОДПИСИ, ИДЫ, ПОРЯДОК, числоРу, датаРу, шагСрезов,
 *          строкаШага, строкаМатрицы, строкаИзображения,
 *          сведенияСерии, пустыеСведения
 */

import { planeNormal, projectOnNormal } from '../core/sliceDepth.js';

/** Подписи строк — ровно те, что стоят заготовками в index.html. */
export const ПОДПИСИ = Object.freeze({
  пациент: 'Пациент',
  исследование: 'Исследование',
  серия: 'Серия',
  изображение: 'Изображение',
  шаг: 'Шаг',
  матрица: 'Матрица',
});

/** Идентификаторы строк на странице. У мобильного ящика — те же с «-m». */
export const ИДЫ = Object.freeze({
  пациент: 'info-patient',
  исследование: 'info-study',
  серия: 'info-series',
  изображение: 'info-image',
  шаг: 'info-spacing',
  матрица: 'info-matrix',
});

export const ПОРЯДОК = Object.freeze(['пациент', 'исследование', 'серия', 'изображение', 'шаг', 'матрица']);

const ПРОЧЕРК = '—';
const строка = (ключ, значение) => `${ПОДПИСИ[ключ]}: ${значение || ПРОЧЕРК}`;

/**
 * Число с десятичной запятой: 0.78125 → «0,781». Хвостовые нули срезаются, но
 * не меньше `минЗнаков` после запятой: 0.5 → «0,50», 1 → «1,00».
 */
export function числоРу(v, знаков = 2, минЗнаков = знаков) {
  const ч = Number(v);
  if (!Number.isFinite(ч)) return '';
  let т = ч.toFixed(знаков);
  if (знаков > минЗнаков) {
    const [цел, дробь = ''] = т.split('.');
    let д = дробь;
    while (д.length > минЗнаков && д.endsWith('0')) д = д.slice(0, -1);
    т = д ? `${цел}.${д}` : цел;
  }
  return т.replace('.', ',');
}

/** Дата DICOM «ГГГГММДД» → «ДД.ММ.ГГГГ». Непонятное — пустая строка. */
export function датаРу(d) {
  const s = String(d ?? '').trim();
  if (!/^\d{8}$/.test(s)) return '';
  return `${s.slice(6, 8)}.${s.slice(4, 6)}.${s.slice(0, 4)}`;
}

/**
 * Интервал между срезами — ПО ПОЗИЦИЯМ СРЕЗОВ, а не по тегу толщины.
 *
 * Толщина среза и шаг между срезами — разные величины: серия 1,0 мм может
 * идти с шагом 0,8 мм (перекрытие) или 1,5 мм (пропуски). Подменять одно
 * другим нельзя — именно такую подмену врач примет за правду. Поэтому шаг
 * считается честно: каждая позиция проецируется на нормаль к плоскости
 * среза, глубины сортируются, берётся медиана соседних разностей.
 *
 * Совпадающие позиции (разность меньше 0,01 мм) пропускаются: так бывает у
 * многофазных и многоэховых серий, где на одном уровне лежит несколько
 * кадров. Иначе минимальный шаг выходил бы нулевым.
 *
 * Неровность — если самая большая разность отличается от самой маленькой
 * больше чем на 0,02 мм или 2% шага. Допуск подобран под погрешность
 * записи координат в файлах, а не взят из стандарта.
 *
 * @param {Array<number[]>} позиции — ImagePositionPatient каждого кадра
 * @param {number[]} ориентация — ImageOrientationPatient (шесть чисел)
 * @returns {{мм:number, ровный:boolean, мин:number, макс:number, уровней:number}|null}
 */
export function шагСрезов(позиции, ориентация) {
  const n = planeNormal(ориентация);
  if (!n || !Array.isArray(позиции)) return null;
  const глубины = [];
  for (const p of позиции) {
    const d = projectOnNormal(p, n);
    if (d !== null) глубины.push(d);
  }
  if (глубины.length < 2) return null;
  глубины.sort((a, b) => a - b);
  const разности = [];
  for (let i = 1; i < глубины.length; i++) {
    const r = глубины[i] - глубины[i - 1];
    if (r >= 0.01) разности.push(r);
  }
  if (!разности.length) return null;
  const упор = [...разности].sort((a, b) => a - b);
  const середина = упор.length >> 1;
  const мм = упор.length % 2 ? упор[середина] : (упор[середина - 1] + упор[середина]) / 2;
  const мин = упор[0], макс = упор[упор.length - 1];
  const допуск = Math.max(0.02, мм * 0.02);
  return { мм, ровный: макс - мин <= допуск, мин, макс, уровней: разности.length + 1 };
}

/**
 * Строка «Шаг»: размер пикселя и интервал между срезами.
 *
 * @param {object} д
 * @param {number[]} [д.пиксель] — PixelSpacing: [между рядами, между столбцами]
 * @param {ReturnType<typeof шагСрезов>} [д.срезы] — результат шагСрезов
 * @param {number} [д.интервалТег] — SpacingBetweenSlices, если позиций нет
 * @param {number} [д.кадров] — сколько кадров в серии
 */
export function строкаШага({ пиксель, срезы, интервалТег, кадров } = {}) {
  const части = [];
  const [пр, пс] = Array.isArray(пиксель) ? пиксель.map(Number) : [];
  if (Number.isFinite(пр) && пр > 0) {
    const ст = Number.isFinite(пс) && пс > 0 ? пс : пр;
    части.push(`${числоРу(пр, 3, 2)} × ${числоРу(ст, 3, 2)} мм`);
  }
  if (Number(кадров) !== 1) {
    if (срезы && Number.isFinite(срезы.мм)) {
      части.push(срезы.ровный
        ? `между срезами ${числоРу(срезы.мм)} мм`
        : `между срезами ${числоРу(срезы.мм)} мм, неровно: ${числоРу(срезы.мин)}–${числоРу(срезы.макс)}`);
    } else if (Number.isFinite(Number(интервалТег)) && Number(интервалТег) > 0) {
      // Позиций нет (многокадровый файл и т.п.) — тогда тег, но с пометкой,
      // откуда число: тег пишет аппарат, и проверить его здесь нечем.
      части.push(`между срезами ${числоРу(интервалТег)} мм (по тегу)`);
    }
  }
  return строка('шаг', части.join(', '));
}

/**
 * Строка «Матрица»: Rows×Columns — в том же порядке, что и отчёт о качестве
 * (studyQualityAnalyzer), чтобы два места программы не спорили между собой.
 */
export function строкаМатрицы(строк, столбцов) {
  const r = Number(строк), c = Number(столбцов);
  return строка('матрица', r > 0 && c > 0 ? `${r}×${c}` : '');
}

/**
 * Строка «Изображение»: номер среза в окне и сколько их всего.
 *
 * @param {object} д
 * @param {number} [д.номер] — с единицы
 * @param {number} [д.всего]
 * @param {boolean} [д.реформат] — окно MPR: номер реформата, не кадра серии
 * @param {number} [д.номерDicom] — InstanceNumber кадра, если отличается от номера
 * @param {boolean} [д.объём3D] — объёмное окно: срезов у него нет
 */
export function строкаИзображения({ номер, всего, реформат, номерDicom, объём3D } = {}) {
  if (объём3D) return строка('изображение', 'объём 3D');
  const н = Number(номер);
  if (!Number.isFinite(н) || н < 1) return строка('изображение', '');
  const в = Number(всего);
  let т = `${н}${Number.isFinite(в) && в > 0 ? ' / ' + в : ''}`;
  if (реформат) т = `реформат ${т}`;
  else if (Number.isFinite(Number(номерDicom)) && Number(номерDicom) !== н) т += ` · № ${Number(номерDicom)}`;
  return строка('изображение', т);
}

/**
 * Строки серии — всё, кроме «Изображения» (оно меняется при листании и
 * пишется отдельно, строкаИзображения).
 *
 * @param {object} с
 * @param {string} [с.пациент] — уже приведённое к виду имя (formatPatientName)
 * @param {string} [с.описаниеИсследования]
 * @param {string} [с.датаИсследования] — «ГГГГММДД»
 * @param {string} [с.описаниеСерии]
 * @param {string|number} [с.номерСерии]
 * @param {string} [с.модальность]
 * @param {number} [с.строк]
 * @param {number} [с.столбцов]
 * @param {number[]} [с.пиксель]
 * @param {Array<number[]>} [с.позиции]
 * @param {number[]} [с.ориентация]
 * @param {number} [с.интервалТег]
 * @param {number} [с.кадров]
 */
export function сведенияСерии(с = {}) {
  const чисто = (v) => String(v ?? '').trim();
  const исследование = [чисто(с.описаниеИсследования), датаРу(с.датаИсследования)].filter(Boolean).join(' · ');
  const описание = чисто(с.описаниеСерии) || чисто(с.модальность);
  const номер = чисто(с.номерСерии);
  const серия = [описание, номер ? `№ ${номер}` : ''].filter(Boolean).join(' · ');
  return {
    пациент: строка('пациент', чисто(с.пациент)),
    исследование: строка('исследование', исследование),
    серия: строка('серия', серия),
    шаг: строкаШага({
      пиксель: с.пиксель,
      срезы: шагСрезов(с.позиции, с.ориентация),
      интервалТег: с.интервалТег,
      кадров: с.кадров,
    }),
    матрица: строкаМатрицы(с.строк, с.столбцов),
  };
}

/** Все шесть строк прочерками — при закрытии исследования и на старте. */
export function пустыеСведения() {
  const out = {};
  for (const к of ПОРЯДОК) out[к] = строка(к, '');
  return out;
}
